// colDef/techProgramColDef.tsx
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ProgramStatus, TechProgramType } from "@/services/techProgram.service";
import { formatDate } from "@/utils/formatDate.util";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<TechProgramType>();

export type TechProgramActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

// ─── Status pill config ───────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  ProgramStatus,
  { label: string; className: string }
> = {
  completed: {
    label: "Completed",
    className: "bg-gray-100 text-gray-600",
  },
  ongoing: {
    label: "Ongoing",
    className: "bg-gray-900 text-white",
  },
  upcoming: {
    label: "Upcoming",
    className: "bg-gray-100 text-gray-600",
  },
};

export const createTechProgramColumns = (
  onActionClick: (payload: TechProgramActionType) => void
) => [
  // Program Name — blue link
  columnHelper.accessor("name", {
    header: "Program Name",
    cell: (info) => (
      <Link
        to={`/activities/tech-skills/${info.row.original._id}`}
        className="font-medium hover:underline"
      >
        {info.getValue()}
      </Link>
    ),
  }),

  // Vendor — plain
  columnHelper.accessor("vendor", {
    header: "Vendor",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Technologies — comma-separated tags
  columnHelper.accessor("technologies", {
    header: "Technologies",
    cell: (info) => {
      const techs = info.getValue();
      return (
        <span className="text-sm">
          {techs.map((tech, i) => (
            <span key={tech}>
              <span className="font-medium">{tech}</span>
              {i < techs.length - 1 && (
                <span className="text-gray-400">, </span>
              )}
            </span>
          ))}
        </span>
      );
    },
  }),

  // Duration — plain
  columnHelper.accessor("duration", {
    header: "Duration",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Enrolled — number
  columnHelper.accessor("enrolled", {
    header: "Enrolled",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Completed — number
  columnHelper.accessor("completed", {
    header: "Completed",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Start Date — YYYY-MM-DD (as shown in image)
  columnHelper.accessor("startDate", {
    header: "Start Date",
    cell: (info) => (
      <span className="whitespace-nowrap text-sm">
        {formatDate(info.getValue(), { locale: "en-CA", dateStyle: "short" })}
      </span>
    ),
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const cfg = STATUS_CONFIG[info.getValue()];
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({ id: data._id, name: data.name, action: "edit" })
            }
            aria-label="Edit program"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({ id: data._id, name: data.name, action: "delete" })
            }
            aria-label="Delete program"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
